import {
    VipsContentLayoutAnalyzer,
} from '@src/slide/application/service/slide-content-layout-analyzer/core/algorithm/vips-content-analyzer';
import { IPresentationModel } from '@src/slide/domain/ui-model/element-modeles/Interface/presentation-model.interface';
import {
    IEnumerateCallback,
    ISlideContentEnumeratorService,
} from '@src/slide/application/service/slide-content-enumerator/slide-content-enumerator-service.interface';
import {
    IElementCallback, IElementNode,
    ISlideCallback,
    ISlideNode, ITextBlockParentBoxNode, ITextParagraphBoxNode,
} from '@src/slide/application/service/slide-rating-and-improve/slide-content-analyzer.interface';
import {
    ContentTree,
} from '@src/slide/application/service/slide-content-layout-analyzer/core/algorithm/data-structure/content-tree';
import {
    ITitleResult,
    TitleAnalyzer,
} from '@src/slide/application/service/slide-content-layout-analyzer/core/algorithm/title-analyzer';
import { TextAnalyzer } from '@src/slide/application/service/slide-content-layout-analyzer/core/algorithm/text-analyzer';
import {
    TextLeafNode,
} from '@src/slide/application/service/slide-content-layout-analyzer/core/algorithm/data-structure/text-leaf-node';
import {
    ISlideContentLayoutAnalyzerService,
} from '@src/slide/application/service/slide-content-layout-analyzer/core/slide-content-layout-analyzer-service.interface';
import {
    PageText,
} from '@src/slide/application/service/slide-content-layout-analyzer/core/algorithm/data-structure/page-content';
import { UiDataService } from '@src/slide/domain/ui-model/service/ui-data-service';

export class SlideContentLayoutAnalyzerService implements ISlideContentLayoutAnalyzerService {
    private analyzer: VipsContentLayoutAnalyzer;
    private titleAnalyzer: TitleAnalyzer;
    private textAnalyzer: TextAnalyzer;
    private enumerator: ISlideContentEnumeratorService;

    public constructor(enumerator: ISlideContentEnumeratorService) {
        this.analyzer = new VipsContentLayoutAnalyzer();
        this.titleAnalyzer = new TitleAnalyzer();
        this.textAnalyzer = new TextAnalyzer();
        this.enumerator = enumerator;
    }

    /**
     * 获取ppt内的文本信息
     * @param presentation
     */
    public async analysisTextResult(presentation: IPresentationModel): Promise<PageText[]> {
        // 先分析页面结构
        await this.analysis(presentation);
        const result = this.getTextResultAfterEnumerateAnalysis(presentation).pagesText;
        this.doAfterAnalysis();
        return result;
    }

    public analysisWithTextResultWhenEnumerateContent(
        presentation: IPresentationModel,
        enumeratorCallbacks: IEnumerateCallback,
        resultCallback: (layoutResult: ContentTree[], textResult: PageText[], titleResult: ITitleResult) => void,
    ) {
        const beforeEnumeratorCallbacks = { ...enumeratorCallbacks };
        enumeratorCallbacks.onStartEnumerate = () => {
            beforeEnumeratorCallbacks.onStartEnumerate?.();
            this.analyzer.resetWithPresentation(presentation);
        };
        enumeratorCallbacks.slideStartCallback = (slideNode: ISlideNode) => {
            beforeEnumeratorCallbacks.slideStartCallback?.(slideNode);
            this.analyzer.analysisNewSlide(slideNode.slide, slideNode.slideIndex);
        };
        enumeratorCallbacks.slideEndCallback = (slideNode: ISlideNode) => {
            beforeEnumeratorCallbacks.slideEndCallback?.(slideNode);
            this.analyzer.analysisSlideEnd(slideNode.slide, slideNode.slideIndex);
        };
        enumeratorCallbacks.elementCallback = (elementNode: IElementNode) => {
            beforeEnumeratorCallbacks.elementCallback?.(elementNode);
            this.analyzer.analysisElement(elementNode);
        };
        enumeratorCallbacks.textBlockParentBoxCallback = (textBlockParentBoxNode: ITextBlockParentBoxNode) => {
            beforeEnumeratorCallbacks.textBlockParentBoxCallback?.(textBlockParentBoxNode);
            this.analyzer.analysisBlockParentBox(textBlockParentBoxNode);
        };
        enumeratorCallbacks.textParagraphParentBoxCallback = (textParagraphBoxNode: ITextParagraphBoxNode) => {
            beforeEnumeratorCallbacks.textParagraphParentBoxCallback?.(textParagraphBoxNode);
            this.analyzer.analysisTextParagraph(textParagraphBoxNode);
        };
        enumeratorCallbacks.presentationEndCallback = (presentation: IPresentationModel) => {
            beforeEnumeratorCallbacks.presentationEndCallback?.(presentation);
            const result = this.getTextResultAfterEnumerateAnalysis(presentation);
            resultCallback(this.analyzer.contentTreeList, result.pagesText, result.titleResult);
            this.doAfterAnalysis();
        };
    }

    private async analysis(
        presentation: IPresentationModel,
        enumerator?: ISlideContentEnumeratorService,
    ): Promise<Array<ContentTree>> {
        this.analyzer.resetWithPresentation(presentation);
        const slideStartCallback: ISlideCallback = (slideNode: ISlideNode) => {
            this.analyzer.analysisNewSlide(slideNode.slide, slideNode.slideIndex);
        };
        const slideEndCallback: ISlideCallback = (slideNode: ISlideNode) => {
            this.analyzer.analysisSlideEnd(slideNode.slide, slideNode.slideIndex);
        };

        const elementCallback: IElementCallback = (elementNode: IElementNode) => {
            this.analyzer.analysisElement(elementNode);
        };

        const textBlockParentBoxCallback = (textBlockParentBoxNode: ITextBlockParentBoxNode) => {
            this.analyzer.analysisBlockParentBox(textBlockParentBoxNode);
        };

        const textParagraphParentBoxCallback = (textParagraphBoxNode: ITextParagraphBoxNode) => {
            this.analyzer.analysisTextParagraph(textParagraphBoxNode);
        };
        const contentEnumerator = enumerator ?? this.enumerator;
        const cloneUiDataService = new UiDataService();
        cloneUiDataService.presentation = presentation;
        await contentEnumerator.enumerateWithCallback({
            slideStartCallback,
            slideEndCallback,
            elementCallback,
            textBlockParentBoxCallback,
            textParagraphParentBoxCallback,
        }, {
            customUiDataService: cloneUiDataService,
        });
        return this.analyzer.contentTreeList;
    }

    private getTextResultAfterEnumerateAnalysis(presentation: IPresentationModel): {
        pagesText: PageText[],
        titleResult: ITitleResult,
    } {
        const { contentTreeList } = this.analyzer;
        // 根据页面结构推测标题区域
        const titleResult = this.titleAnalyzer.analysisByLayout(presentation, contentTreeList);
        // 初步提取文本信息
        const textResult: TextLeafNode[][] = this.textAnalyzer
            .analysisTitleInfo(contentTreeList, titleResult.titleTemplateMap, titleResult.titleTemplateSimilarMap);
        const pagesText: PageText[] = presentation.children
            .map((slideModel, index) => new PageText(slideModel, textResult[index]));
        // 推测并修改页面信息
        this.textAnalyzer.analysisPageInfoByContent(pagesText, presentation, titleResult);
        return { pagesText, titleResult };
    }

    private doAfterAnalysis() {
        this.analyzer.reset();
        this.titleAnalyzer.reset();
    }
}
